import React from "react";
import { Alert, Grid, Paper, Stack, Typography } from "@mui/material";
import { Link as RouterLink, useOutletContext } from "react-router-dom";
import { realEstateApi } from "../../../api/realEstate";
import { formatApiError } from "../../../api/client";
import DashboardLoadingState from "../../../components/dashboard/DashboardLoadingState";

function Metric({ label, value, helper, to }) {
  const interactive = Boolean(to);
  return (
    <Paper
      component={interactive ? RouterLink : "div"}
      to={interactive ? to : undefined}
      sx={{
        display: "block",
        p: 2.5,
        borderRadius: 3,
        cursor: interactive ? "pointer" : "default",
        color: "inherit",
        textDecoration: "none",
        border: "1px solid rgba(var(--wp-panel-shadow-rgb, 25,36,47),.08)",
        boxShadow: "0 10px 30px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.06)",
        "&:focus-visible": interactive ? { outline: "3px solid rgba(243,90,58,.38)", outlineOffset: 2 } : undefined,
      }}
    >
      <Typography sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)", fontWeight: 800 }}>{label}</Typography>
      <Typography sx={{ mt: .5, color: "var(--wp-panel-text-primary, #19242F)", fontWeight: 950, fontSize: 32 }}>{value}</Typography>
      <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{helper}</Typography>
    </Paper>
  );
}

export default function ProjectRealEstateOverviewPage() {
  const context = useOutletContext();
  const { projectId, canReadProperties, canReadLeases, canReadCollections } = context;
  const [state, setState] = React.useState({ loading: true, error: "", properties: null, leases: null, pending: null, overdue: null });

  React.useEffect(() => {
    let cancelled = false;
    const requests = [
      canReadProperties ? realEstateApi.listProperties(projectId, { page: 1, pageSize: 1 }) : Promise.resolve(null),
      canReadLeases ? realEstateApi.listLeases(projectId, { page: 1, pageSize: 1 }) : Promise.resolve(null),
      canReadCollections ? realEstateApi.listObligations(projectId, { page: 1, pageSize: 1, status: "pending" }) : Promise.resolve(null),
      canReadCollections ? realEstateApi.listObligations(projectId, { page: 1, pageSize: 1, status: "overdue" }) : Promise.resolve(null),
    ];
    Promise.all(requests).then(([properties, leases, pending, overdue]) => {
      if (cancelled) return;
      setState({ loading: false, error: "", properties: properties?.pagination?.total ?? null, leases: leases?.pagination?.total ?? null, pending: pending?.pagination?.total ?? null, overdue: overdue?.pagination?.total ?? null });
    }).catch((error) => {
      if (!cancelled) setState((current) => ({ ...current, loading: false, error: formatApiError(error, "No se pudo cargar el resumen inmobiliario.") }));
    });
    return () => { cancelled = true; };
  }, [projectId, canReadProperties, canReadLeases, canReadCollections]);

  if (state.loading) return <DashboardLoadingState label="Cargando resumen inmobiliario…" minHeight={320} />;
  return (
    <Stack spacing={2.5}>
      {state.error ? <Alert severity="error">{state.error}</Alert> : null}
      <Grid container spacing={2}>
        {canReadProperties ? <Grid item xs={12} sm={6} lg={3}><Metric label="Propiedades" value={state.properties ?? "—"} helper="Inmuebles registrados" to="propiedades" /></Grid> : null}
        {canReadLeases ? <Grid item xs={12} sm={6} lg={3}><Metric label="Contratos" value={state.leases ?? "—"} helper="Historial contractual" to="contratos" /></Grid> : null}
        {canReadCollections ? <Grid item xs={12} sm={6} lg={3}><Metric label="Pendientes" value={state.pending ?? "—"} helper="Obligaciones por cobrar" to="cobranzas?estado=pending" /></Grid> : null}
        {canReadCollections ? <Grid item xs={12} sm={6} lg={3}><Metric label="Vencidas" value={state.overdue ?? "—"} helper="Requieren seguimiento" to="cobranzas?estado=overdue" /></Grid> : null}
      </Grid>
    </Stack>
  );
}
